import React, { useState, useEffect } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Building2, Thermometer, Zap, Scale, Leaf, Plus, Trash2 } from 'lucide-react';

const ENUM_OPTIONS = {
  property_type: ['Wonen', 'Kantoor', 'Retail', 'Logistiek/Light industrial', 'Gemengd', 'Overig', 'Onbekend'],
  verhuur_status: ['In exploitatie', 'In ontwikkeling', 'Leeg', 'Renovatie', 'Onbekend'],
  bouwkundige_staat: ['Goed', 'Redelijk', 'Matig', 'Slecht', 'Onbekend'],
  verwarmingssysteem: ['CV gas', 'Stadswarmte', 'Warmtepomp', 'Overig', 'Onbekend'],
  ventilatie_type: ['Natuurlijk', 'Mechanisch', 'WTW', 'Onbekend'],
  eigendomstype: ['Volle eigendom', 'Erfpacht', 'Opstal', 'Appartementsrecht', 'Onbekend'],
  monument_status: ['Geen', 'Gemeentelijk', 'Rijks', 'Onbekend'],
  maatregel_type: ['PV', 'Warmtepomp', 'Isolatie dak', 'Isolatie gevel', 'Glas', 'LED', 'WKO', 'Overig'],
  subsidie_relevant: ['EIA', 'MIA/Vamil', 'ISDE', 'DUMAVA', 'SVOH', 'Geen', 'Onbekend']
};

// Map asset_class to VT15 property_type
const ASSET_CLASS_TO_PROPERTY_TYPE = {
  'residential': 'Wonen',
  'office': 'Kantoor',
  'retail': 'Retail',
  'light_industrial': 'Logistiek/Light industrial',
  'logistics': 'Logistiek/Light industrial',
  'combination': 'Gemengd',
  'other': 'Overig'
};

export const BuildingPassportTab = ({ building, onUpdate, completeness }) => {
  const [form, setForm] = useState(building || {});
  const [maatregelen, setMaatregelen] = useState(building?.verduurzamingsmaatregelen || []);

  useEffect(() => {
    if (building) {
      // Map asset_class to property_type if property_type is not set
      const mappedPropertyType = building.property_type || 
        ASSET_CLASS_TO_PROPERTY_TYPE[building.asset_class] || 
        'Onbekend';
      
      setForm({
        ...building,
        property_type: mappedPropertyType
      });
      setMaatregelen(building.verduurzamingsmaatregelen || []);
    }
  }, [building]);

  const handleChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    onUpdate(updated);
  };

  const addMaatregel = () => {
    const newMaatregel = { maatregel_type: 'Overig', capex_eur: null, opleverdatum: null, subsidie_relevant: 'Onbekend' };
    const updated = [...maatregelen, newMaatregel];
    setMaatregelen(updated);
    handleChange('verduurzamingsmaatregelen', updated);
  };

  const updateMaatregel = (index, field, value) => {
    const updated = [...maatregelen];
    updated[index] = { ...updated[index], [field]: value };
    setMaatregelen(updated);
    handleChange('verduurzamingsmaatregelen', updated);
  };

  const removeMaatregel = (index) => {
    const updated = maatregelen.filter((_, i) => i !== index);
    setMaatregelen(updated);
    handleChange('verduurzamingsmaatregelen', updated);
  };

  return (
    <div className="space-y-4" data-testid="building-passport-tab">
      {/* Completeness indicator */}
      <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-[#b4976f]" />
          <span className="text-slate-300">VT15 Datakwaliteit</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ${completeness >= 70 ? 'bg-green-500' : completeness >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${completeness}%` }}
            />
          </div>
          <Badge variant={completeness >= 70 ? 'default' : completeness >= 40 ? 'secondary' : 'destructive'}>
            {completeness}%
          </Badge>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={['basis', 'fysiek']} className="space-y-2">
        {/* BASIS IDENTIFICATIE */}
        <AccordionItem value="basis" className="bg-slate-800/50 rounded-lg border border-slate-700">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#b4976f]" />
              <span className="text-white font-medium">Basis Identificatie</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Vastgoedtype</Label>
                <Select value={form.property_type || 'Onbekend'} onValueChange={(v) => handleChange('property_type', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ENUM_OPTIONS.property_type.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Verhuur Status</Label>
                <Select value={form.verhuur_status || 'Onbekend'} onValueChange={(v) => handleChange('verhuur_status', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ENUM_OPTIONS.verhuur_status.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* FYSIEKE KENMERKEN */}
        <AccordionItem value="fysiek" className="bg-slate-800/50 rounded-lg border border-slate-700">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-400" />
              <span className="text-white font-medium">Fysieke Kenmerken</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Bouwjaar</Label>
                <Input type="number" value={form.bouwjaar || ''} onChange={(e) => handleChange('bouwjaar', e.target.value ? parseInt(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Renovatiejaar</Label>
                <Input type="number" value={form.renovatiejaar_laatste || ''} onChange={(e) => handleChange('renovatiejaar_laatste', e.target.value ? parseInt(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Aantal Bouwlagen</Label>
                <Input type="number" value={form.aantal_bouwlagen || ''} onChange={(e) => handleChange('aantal_bouwlagen', e.target.value ? parseInt(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Perceel m²</Label>
                <Input type="number" value={form.perceel_oppervlakte_m2 || ''} onChange={(e) => handleChange('perceel_oppervlakte_m2', e.target.value ? parseFloat(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">GBO m²</Label>
                <Input type="number" value={form.gbo_m2 || ''} onChange={(e) => handleChange('gbo_m2', e.target.value ? parseFloat(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Bouwkundige Staat</Label>
                <Select value={form.bouwkundige_staat || 'Onbekend'} onValueChange={(v) => handleChange('bouwkundige_staat', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ENUM_OPTIONS.bouwkundige_staat.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* INSTALLATIES */}
        <AccordionItem value="installaties" className="bg-slate-800/50 rounded-lg border border-slate-700">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Thermometer className="h-4 w-4 text-orange-400" />
              <span className="text-white font-medium">Installaties & Voorzieningen</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Verwarmingssysteem</Label>
                <Select value={form.verwarmingssysteem || 'Onbekend'} onValueChange={(v) => handleChange('verwarmingssysteem', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ENUM_OPTIONS.verwarmingssysteem.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Ventilatie Type</Label>
                <Select value={form.ventilatie_type || 'Onbekend'} onValueChange={(v) => handleChange('ventilatie_type', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ENUM_OPTIONS.ventilatie_type.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                <Label className="text-slate-300">Koeling</Label>
                <Switch checked={form.koeling_aanwezig || false} onCheckedChange={(v) => handleChange('koeling_aanwezig', v)} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                <Label className="text-slate-300">PV</Label>
                <Switch checked={form.pv_aanwezig || false} onCheckedChange={(v) => handleChange('pv_aanwezig', v)} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                <Label className="text-slate-300">Laadpalen</Label>
                <Switch checked={form.laadpalen_aanwezig || false} onCheckedChange={(v) => handleChange('laadpalen_aanwezig', v)} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                <Label className="text-slate-300">Sprinkler</Label>
                <Switch checked={form.sprinkler_aanwezig || false} onCheckedChange={(v) => handleChange('sprinkler_aanwezig', v)} />
              </div>
            </div>
            {form.pv_aanwezig && (
              <div className="mt-3 space-y-2">
                <Label className="text-slate-300">PV Vermogen (kWp)</Label>
                <Input type="number" value={form.pv_vermogen_kwp || ''} onChange={(e) => handleChange('pv_vermogen_kwp', e.target.value ? parseFloat(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white w-48" />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* ENERGIE */}
        <AccordionItem value="energie" className="bg-slate-800/50 rounded-lg border border-slate-700">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-400" />
              <span className="text-white font-medium">Energie & Duurzaamheid</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Energieverbruik (kWh/jaar)</Label>
                <Input type="number" value={form.energieverbruik_kwh_per_jaar || ''} onChange={(e) => handleChange('energieverbruik_kwh_per_jaar', e.target.value ? parseFloat(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Gasverbruik (m³/jaar)</Label>
                <Input type="number" value={form.gasverbruik_m3_per_jaar || ''} onChange={(e) => handleChange('gasverbruik_m3_per_jaar', e.target.value ? parseFloat(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
              </div>
            </div>
            {form.energy_labels && form.energy_labels.length > 0 && (
              <div className="mt-4 p-3 bg-slate-900 rounded-lg">
                <Label className="text-slate-300 mb-2 block">EP-Online Energielabels</Label>
                <div className="flex flex-wrap gap-2">
                  {form.energy_labels.map((el, idx) => (
                    <Badge key={idx} className="bg-[#b4976f] text-white">VBO ...{el.bag_vbo_id?.slice(-4)}: {el.energy_label}</Badge>
                  ))}
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* JURIDISCH */}
        <AccordionItem value="juridisch" className="bg-slate-800/50 rounded-lg border border-slate-700">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-purple-400" />
              <span className="text-white font-medium">Juridisch & Eigendom</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Eigendomstype</Label>
                <Select value={form.eigendomstype || 'Onbekend'} onValueChange={(v) => handleChange('eigendomstype', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ENUM_OPTIONS.eigendomstype.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Monument Status</Label>
                <Select value={form.monument_status || 'Geen'} onValueChange={(v) => handleChange('monument_status', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ENUM_OPTIONS.monument_status.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 p-3 bg-slate-900 rounded-lg">
              <Label className="text-slate-300">Erfpacht van Toepassing</Label>
              <Switch checked={form.erfpacht_van_toepassing || false} onCheckedChange={(v) => handleChange('erfpacht_van_toepassing', v)} />
            </div>
            {form.erfpacht_van_toepassing && (
              <div className="mt-3 grid grid-cols-2 gap-4 pl-4 border-l-2 border-[#b4976f]">
                <div className="space-y-2">
                  <Label className="text-slate-300">Erfpacht Canon (€/jaar)</Label>
                  <Input type="number" value={form.erfpacht_canon_per_jaar || ''} onChange={(e) => handleChange('erfpacht_canon_per_jaar', e.target.value ? parseFloat(e.target.value) : null)} className="bg-slate-900 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Erfpacht Einddatum</Label>
                  <Input type="date" value={form.erfpacht_einddatum?.split('T')[0] || ''} onChange={(e) => handleChange('erfpacht_einddatum', e.target.value)} className="bg-slate-900 border-slate-700 text-white" />
                </div>
              </div>
            )}
            <div className="mt-4 space-y-2">
              <Label className="text-slate-300">Bestemming</Label>
              <Input value={form.bestemming || ''} onChange={(e) => handleChange('bestemming', e.target.value)} className="bg-slate-900 border-slate-700 text-white" placeholder="Bijv. Wonen, Kantoor, Gemengd" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* VERDUURZAMINGSMAATREGELEN */}
        <AccordionItem value="maatregelen" className="bg-slate-800/50 rounded-lg border border-slate-700">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Leaf className="h-4 w-4 text-green-400" />
              <span className="text-white font-medium">Verduurzamingsmaatregelen</span>
              {maatregelen.length > 0 && <Badge variant="secondary" className="ml-2">{maatregelen.length}</Badge>}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-3">
              {maatregelen.map((maatregel, idx) => (
                <div key={idx} className="grid grid-cols-5 gap-3 p-3 bg-slate-900 rounded-lg items-end">
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Type</Label>
                    <Select value={maatregel.maatregel_type} onValueChange={(v) => updateMaatregel(idx, 'maatregel_type', v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {ENUM_OPTIONS.maatregel_type.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">CAPEX (€)</Label>
                    <Input type="number" value={maatregel.capex_eur || ''} onChange={(e) => updateMaatregel(idx, 'capex_eur', e.target.value ? parseFloat(e.target.value) : null)} className="bg-slate-800 border-slate-700 text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Opleverdatum</Label>
                    <Input type="date" value={maatregel.opleverdatum?.split('T')[0] || ''} onChange={(e) => updateMaatregel(idx, 'opleverdatum', e.target.value)} className="bg-slate-800 border-slate-700 text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Subsidie</Label>
                    <Select value={maatregel.subsidie_relevant || 'Onbekend'} onValueChange={(v) => updateMaatregel(idx, 'subsidie_relevant', v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {ENUM_OPTIONS.subsidie_relevant.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeMaatregel(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-400/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addMaatregel} className="border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-[#b4976f]">
                <Plus className="h-4 w-4 mr-2" />
                Maatregel Toevoegen
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default BuildingPassportTab;
